import { trainingZoneColorForLabel } from '../../utils/trainingZones';
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoHeartOutline, IoSpeedometerOutline, IoCreateOutline } from 'react-icons/io5';
import { NmButton } from '../NmButton';
import { CustomZoneBuilder, type ZoneRow } from './ZoneCustomization';
import type { ZoneMethod } from '../../services/athleteSettingsApi';
import { displayInputDigits, normalizeInputDigits } from '../../utils/localizedInput';
import './power-zone-editor.css';
import { trainingZoneScale, trainingZoneFill } from '../../utils/trainingZoneScale';

export function zoneMethodName(method: ZoneMethod, language: string): string {
  let name = method.name;
  if (language.startsWith('fa')) {
    const names: Record<string, string> = {
      'BCF/ABCC/WCPP Revised': 'روش بازنگری‌شدهٔ فدراسیون دوچرخه‌سواری بریتانیا',
      'Peter Keen': 'پیتر کین', 'Ric Stern': 'ریک استرن', 'Sally Edwards': 'سالی ادواردز',
      'Timex Manual Entry': 'تایمکس، ورود دستی', 'Timex': 'تایمکس', 'ACSM': 'کالج پزشکی ورزشی آمریکا', 'Linear': 'خطی',
      'PZI': 'پی‌زد‌آی', 'for': 'برای', 'CycleSmart': 'سایکل‌اسمارت', 'USAC': 'یواس‌ای‌سی',
      'Joe Friel': 'جو فریل', 'Andy Coggan': 'اندی کاگان', 'Jack Daniels': 'جک دنیلز',
      'Daniels': 'دنیلز', 'TrainingPeaks': 'ترینینگ‌پیکس', 'British Cycling': 'دوچرخه‌سواری بریتانیا',
      'Lactate Threshold': 'آستانهٔ لاکتات', 'Threshold Heart Rate': 'ضربان آستانه',
      'Max Heart Rate': 'حداکثر ضربان', 'Maximum Heart Rate': 'حداکثر ضربان',
      'Heart Rate Reserve': 'ذخیرهٔ ضربان', 'Threshold Pace': 'پیس آستانه',
      'MyProCoach': 'مای‌پروکوچ', 'Durata Training': 'دوراتا', 'Karvonen': 'کاروونن',
      'Stryd': 'استراید', 'CTS': 'سی‌تی‌اس', 'USAT': 'یو‌اس‌ای‌تی',
      'Running': 'دویدن', 'Cycling': 'دوچرخه‌سواری', 'Swimming': 'شنا',
      'Run': 'دویدن', 'Bike': 'دوچرخه', 'Swim': 'شنا', 'Friel': 'فریل',
      'Active Recovery': 'ریکاوری فعال', 'Aerobic Capacity': 'ظرفیت هوازی',
      'Anaerobic Capacity': 'ظرفیت بی‌هوازی', 'Endurance': 'استقامت', 'Recovery': 'ریکاوری',
      'Threshold': 'آستانه', 'Tempo': 'تمپو', 'VO2 Max': 'توان هوازی بیشینه', 'Maximum': 'بیشینه',
      'Zones': 'نواحی', 'Zone': 'ناحیه', 'LTHR': 'ضربان آستانه', 'HRmax': 'حداکثر ضربان',
      'MHR': 'حداکثر ضربان', 'HRR': 'ذخیرهٔ ضربان', 'HR': 'ضربان',
    };
    for (const [source, translated] of Object.entries(names)) name = name.replaceAll(source, translated);
  }
  return displayInputDigits(name, language).replace(/^زون(?=\s+[۰-۹0-9])/, 'ناحیه');
}

export function TrainingZoneField({ label, value, onChange, kind = 'number', placeholder, unit }: {
  label: string; value: string; onChange: (value: string) => void; kind?: 'number' | 'pace'; placeholder?: string; unit?: string;
}) {
  const { i18n } = useTranslation();
  const id = useId();
  const input = <input id={id} type="text" inputMode={kind === 'number' ? 'decimal' : 'text'} dir="ltr" autoComplete="off"
      aria-describedby={unit ? `${id}-unit` : undefined}
      value={displayInputDigits(value, i18n.language)}
      placeholder={placeholder ? displayInputDigits(placeholder, i18n.language) : undefined}
      onChange={event => {
        const next = normalizeInputDigits(event.target.value).replace('٫', '.');
        if ((kind === 'pace' ? /^[0-9:]*$/ : /^\d*(?:\.\d*)?$/).test(next)) onChange(next);
      }} />;
  return <div className="power-zone-editor__field">
    <label htmlFor={id}>{label}</label>
    {unit ? <div className="power-zone-editor__ftp zone-custom-builder__unit-input"><span id={`${id}-unit`} dir="rtl">{unit}</span>{input}</div> : input}
  </div>;
}

/** Keep the numeric range isolated from the unit in bidirectional layouts. */
export function TrainingZoneRange({ value, unit }: { value: string; unit: string }) {
  const { i18n } = useTranslation();
  const rtl = /^(fa|ar)/.test(i18n.language);
  return <span className="training-zone-editor__value-unit" dir={rtl ? 'rtl' : 'ltr'}>
    <bdi dir="ltr">{displayInputDigits(value, i18n.language)}</bdi><span>{unit}</span>
  </span>;
}

interface Props {
  kind: 'heart' | 'pace'; settings: React.ReactNode; customFields: React.ReactNode;
  customThreshold?: { value: string; label: string };
  methods: ZoneMethod[]; methodId: number | ''; onMethodChange: (value: number | '') => void;
  zones: ZoneRow[]; unit: string; formatRange: (zone: ZoneRow) => React.ReactNode;
  busy: boolean; savingLabels: boolean; onCalculate: () => Promise<void>;
  onSaveLabels: (zones: ZoneRow[]) => Promise<void>; onSaveCustom: (zones: ZoneRow[]) => Promise<void>;
  savedSummary?: React.ReactNode;
}

export function TrainingZoneEditor(props: Props) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [editing, setEditing] = useState<number | null>(null);
  const [labels, setLabels] = useState<Record<number, string>>({});
  const [error, setError] = useState('');
  const localize = (value: string | number) => displayInputDigits(String(value), i18n.language);
  const copy = (key: string) => t(`athleteSettings.trainingEditor.${key}`);
  const shared = (key: string) => t(`athleteSettings.powerEditor.${key}`);
  const scale = trainingZoneScale(props.kind, props.zones);
  const Icon = props.kind === 'heart' ? IoHeartOutline : IoSpeedometerOutline;
  async function save() {
    try {
      setError('');
      await props.onSaveLabels(props.zones.map((zone, index) => ({ ...zone, label: labels[index] ?? zone.label })));
      setLabels({}); setEditing(null);
    } catch { setError(shared('saveFailed')); }
  }
  return <div className="power-zone-editor training-zone-editor">
    <header className="power-zone-editor__heading">
      <span className="power-zone-editor__icon"><Icon aria-hidden="true" /></span>
      <div><h2>{copy(`${props.kind}Title`)}</h2></div>
    </header>
    <section className="power-zone-editor__settings" aria-label={copy(`${props.kind}Title`)}>
      {props.kind === 'heart' && props.settings}
      <div className="power-zone-editor__field">
        <label htmlFor={`${id}-method`}>{shared('method')}</label>
        <select id={`${id}-method`} value={props.methodId} onChange={event => props.onMethodChange(event.target.value ? Number(event.target.value) : '')}>
          <option value="">{shared('chooseMethod')}</option>
          {props.methods.map(method => <option key={method.id} value={method.id}>{zoneMethodName(method, i18n.language)}</option>)}
        </select>
      </div>
      {props.kind === 'pace' && props.settings}
      {props.savedSummary && <div className="training-zone-editor__summary">{props.savedSummary}</div>}
    </section>
    <section aria-labelledby={`${id}-zones`} aria-busy={props.busy}>
      <div className="power-zone-editor__section-heading"><h3 id={`${id}-zones`}>{shared('zones')}</h3>
        <span>{localize(t('athleteSettings.powerEditor.count', { count: props.zones.length }))}</span></div>
      {props.zones.length ? <ol className="power-zone-editor__list">
        {props.zones.map((zone, index) => {
          const automatic = /^Zone\s+\d+[\s:\-\d%]*$/i.test(normalizeInputDigits(zone.label));
          const name = labels[index] ?? (automatic ? t('athleteSettings.powerEditor.zone', { number: localize(index + 1) }) : zoneMethodName({ id: index, name: zone.label }, i18n.language));
          return <li key={index} className="power-zone-editor__zone" style={{ '--zone-start': '0%', '--zone-fill': `${trainingZoneFill(zone.maximum, scale.factor, scale.maximum)}%`, '--zone-threshold': '50%', '--zone-color': trainingZoneColorForLabel(zone.label ?? '', index) } as React.CSSProperties}>
            <span className="power-zone-editor__number" aria-hidden="true">{localize(index + 1)}</span>
            <div className="power-zone-editor__zone-content">
              <div className="power-zone-editor__zone-top">
                {editing === index ? <input autoFocus className="power-zone-editor__label-input" aria-label={shared('editName')}
                  value={localize(labels[index] ?? zone.label)} onChange={event => setLabels(previous => ({ ...previous, [index]: event.target.value }))}
                  onBlur={() => setEditing(null)} onKeyDown={event => { if (event.key === 'Enter' || event.key === 'Escape') setEditing(null); }} />
                  : <button type="button" className="power-zone-editor__name" title={shared('editName')} onClick={() => setEditing(index)}>{name}<span className="power-zone-editor__edit-icon"><IoCreateOutline aria-hidden="true" /></span></button>}
                <span className="power-zone-editor__range training-zone-editor__range" >{props.formatRange(zone)}</span>
              </div>
              <div className="power-zone-editor__plot training-zone-editor__plot">
                <div className="power-zone-editor__track" aria-hidden="true"><span /></div>
              </div>
            </div>
          </li>;
        })}
      </ol> : <p className="power-zone-editor__empty">{copy('empty')}</p>}
      {error && <p className="power-zone-editor__error" role="alert">{error}</p>}
      {Object.keys(labels).length > 0 && <NmButton onClick={() => void save()} disabled={props.savingLabels}>{shared(props.savingLabels ? 'saving' : 'saveNames')}</NmButton>}
    </section>
    <NmButton className="power-zone-editor__calculate" variant="primary" disabled={props.busy} onClick={() => void props.onCalculate()}>{shared(props.busy ? 'calculating' : 'calculate')}</NmButton>
    <div className="power-zone-editor__custom"><CustomZoneBuilder unit={props.unit} thresholdFields={props.customFields} percentageThreshold={props.customThreshold} onSave={props.onSaveCustom} /></div>
  </div>;
}
